import { WsConnectionStatus, wsStore } from "@shared/api/websocket"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"
import { useEffect, useSyncExternalStore } from "react"
import { watchBootSnapshot } from "@/app/boot/snapshot"
import { useTrackUser } from "@/app/providers/analytics/user"
import { initPushNotifications } from "@/app/push-notifications"
import { BottomPopup, NotificationOverlay } from "@/app/ui"
import {
	useCurrentUser,
	userQueryOptions,
	useSyncUserTimezone,
} from "@/entities/user"
import { useHandleInvitation } from "@/features/invite-friend"
import { useRecoverUnfinishedTransactions } from "@/features/purchase"
import { getSessionVersion, subscribeSessionVersion } from "@/shared/lib/auth"
import { MonsterSubscriptionDialog } from "@/widgets/monster-subscription-promo"

export const Route = createFileRoute("/_authenticated")({
	beforeLoad: ({ context }) => {
		if (!context.isAuthenticated || context.userId === null) {
			throw redirect({
				to: "/onboarding",
				replace: true,
			})
		}
		return { sessionVersion: getSessionVersion(), userId: context.userId }
	},
	loader: async ({ context }) => {
		wsStore.connect(String(context.userId))
		await context.queryClient.query({
			...userQueryOptions(context.userId),
			staleTime: "static",
		})
	},
	component: AuthenticatedLayout,
})

function useWatchBootSnapshot(userId: number) {
	const queryClient = useQueryClient()

	useEffect(() => {
		return watchBootSnapshot(queryClient, userId)
	}, [userId, queryClient])
}

function AuthenticatedLayout() {
	const { userId, sessionVersion } = Route.useRouteContext()
	const currentSessionVersion = useSyncExternalStore(
		subscribeSessionVersion,
		getSessionVersion,
	)

	if (currentSessionVersion !== sessionVersion) return null

	return <AuthenticatedSession userId={userId} />
}

function AuthenticatedSession({ userId }: { userId: number }) {
	useHandleInvitation(userId)
	useTrackUser(userId)
	useSyncUserTimezone(useCurrentUser())
	useRecoverUnfinishedTransactions(userId)
	useWatchBootSnapshot(userId)

	// Register FCM token (Android/iOS only, after auth). Runs once on mount.
	useEffect(() => {
		initPushNotifications().catch(console.error)
	}, [])

	return (
		<>
			<WsConnectionStatus />
			<Outlet />
			<BottomPopup />
			<NotificationOverlay />
			<MonsterSubscriptionDialog />
		</>
	)
}
